/** Display formatting for the site: amounts, periods, and identifiers as a reader sees them. Nothing here formats a literal for the graph; that is `src/format`. */
const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const integers = new Intl.NumberFormat('en-US');

export const formatAmount = (amount: number | null): string => (amount === null ? 'Amount not stated' : dollars.format(amount));

export const formatCount = (count: number): string => integers.format(count);

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** A tax period `YYYYMM` reads as the month the period ended; a fiscal-year label or a date reads as itself. */
export const formatPeriod = (period: string | null): string => {
  if (period === null) {
    return 'Period not stated';
  }
  if (/^\d{6}$/.test(period)) {
    const month = MONTHS[Number(period.slice(4)) - 1];
    return month === undefined ? period : `${month} ${period.slice(0, 4)}`;
  }
  if (/^\d{4}$/.test(period)) {
    return `fiscal year ${period}`;
  }
  return period;
};

/** A run identifier `2026-09-15T09-18-45-742Z` read as the day it was made; anything else is shown as written. */
export const buildDate = (runId: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T/.exec(runId);
  if (match === null) {
    return runId;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
};

export const formatEin = (ein: string): string => (ein.length === 9 ? `${ein.slice(0, 2)}-${ein.slice(2)}` : ein);

export const SOURCE_LABELS: Readonly<Record<string, string>> = {
  'irs-bmf': 'IRS Exempt Organizations Business Master File',
  'irs-990': 'IRS Form 990 e-file',
  web: 'Funder website',
  usaspending: 'USAspending',
  fac: 'Federal Audit Clearinghouse',
};

export const sourceLabel = (source: string): string => SOURCE_LABELS[source] ?? source;

/** The last segment of an identifier, for a compact display of a filing or grant. */
export const identifierTail = (iri: string): string => iri.slice(iri.indexOf('/id/') + 4);

/** A span of tax periods, "August 2024 to August 2025"; one period reads as itself. */
export const formatPeriodRange = (first: string | null, last: string | null): string => {
  const from = first ?? last;
  const to = last ?? first;
  if (from === null || to === null) {
    return 'no period on record';
  }
  return from === to ? formatPeriod(from) : `${formatPeriod(from)} to ${formatPeriod(to)}`;
};

// ProPublica's Nonprofit Explorer serves every e-filed return by filer EIN and IRS
// object identifier; the pattern was verified to resolve on 2026-10-11. It is the
// one public page a reader can open to see the return the graph read.
export const returnUrl = (filerEin: string, objectId: string): string => `https://projects.propublica.org/nonprofits/organizations/${filerEin}/${objectId}/full`;
